import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, Bot, CheckCircle2, Code2, ExternalLink, Globe, Inbox, Mail, Pause, Phone, Play, RotateCcw, Send, UserRound } from 'lucide-react';
import { format, formatDistanceToNowStrict } from 'date-fns';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { SearchableSelect } from '@/components/SearchableSelect';
import {
  aiAgentsService, AiAgent, AiConversation, AiMessage, ConversationFilter, PAUSE_REASON_LABEL,
} from '@/services/ai-agents.service';

type StatusTab = 'needs_team' | 'open' | 'closed' | 'all';
const TABS: Array<{ id: StatusTab; label: string }> = [
  { id: 'needs_team', label: 'Needs team' },
  { id: 'open', label: 'Open' },
  { id: 'closed', label: 'Closed' },
  { id: 'all', label: 'All' },
];

const who = (c: AiConversation) => c.collectedFields?.customer_name || c.visitor?.name || c.visitor?.phone || c.visitor?.email || 'Visitor';

/**
 * The open chat lives in `?c=` rather than the path: pages are keyed by pathname,
 * so a path per chat would remount (and blank) the whole inbox on every click.
 */
export default function AiConversations() {
  const { conversationUid: pathUid } = useParams();
  if (pathUid) return <Navigate to={`/ai-agents/conversations?c=${encodeURIComponent(pathUid)}`} replace />;
  return <ConversationsInbox />;
}

function ConversationsInbox() {
  const [params, setParams] = useSearchParams();
  const conversationUid = params.get('c') || undefined;
  const tab = (params.get('status') as StatusTab) || 'open';
  const agentUid = params.get('agent') || '';
  const channel = (params.get('channel') as ConversationFilter['channel']) || undefined;
  const [agents, setAgents] = useState<AiAgent[]>([]);
  const [rows, setRows] = useState<AiConversation[] | null>(null);

  const setParam = (k: string, v?: string, push = false) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: !push });
  };

  useEffect(() => { aiAgentsService.list().then(setAgents).catch(() => setAgents([])); }, []);

  const load = useCallback(() => {
    const q: ConversationFilter = { ...(agentUid ? { agentUid } : {}), ...(channel ? { channel } : {}), ...(tab !== 'all' ? { status: tab } : {}) };
    return aiAgentsService.conversations(q).then(setRows).catch(() => setRows((r) => r ?? []));
  }, [agentUid, channel, tab]);

  useEffect(() => {
    setRows(null);
    void load();
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [load]);

  const agentName = useMemo(() => new Map(agents.map((a) => [a.agentUid, a.name])), [agents]);
  const open = (uid: string) => setParam('c', uid, true);

  // Fill what the topbar leaves (it is taller on phones) so the reply box never falls below the fold.
  return (
    <div className="flex h-full min-h-[520px] flex-col">
      <Topbar title="Conversations" breadcrumb="AI Agents / Conversations" />
      <div className="flex min-h-0 w-full flex-1">
        <aside className={clsx('flex w-full shrink-0 flex-col border-r border-border bg-surface md:w-[340px]', conversationUid && 'hidden md:flex')}>
          <div className="space-y-2 border-b border-border p-3">
            <div className="flex gap-1 rounded-xl bg-surface-alt p-1">
              {TABS.map((t) => (
                <button key={t.id} type="button" onClick={() => setParam('status', t.id === 'open' ? undefined : t.id)}
                  className={clsx('flex-1 whitespace-nowrap rounded-lg px-1.5 py-1.5 text-xs font-medium transition-colors', tab === t.id ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted hover:text-text-primary')}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <SearchableSelect
                options={[{ value: '', label: 'All agents' }, ...agents.map((a) => ({ value: a.agentUid, label: a.name }))]}
                value={agentUid} onChange={(v) => setParam('agent', v || undefined)} numbered={false} searchable={agents.length > 6} aria-label="Agent"
              />
              <SearchableSelect
                options={[{ value: '', label: 'All channels' }, { value: 'web', label: 'Website' }, { value: 'api', label: 'App (API)' }]}
                value={channel ?? ''} onChange={(v) => setParam('channel', v || undefined)} numbered={false} searchable={false} aria-label="Channel"
              />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {rows === null ? (
              <div className="space-y-2 p-3">{[1, 2, 3, 4].map((n) => <div key={n} className="skeleton h-16 rounded-xl" />)}</div>
            ) : rows.length === 0 ? (
              <div className="flex flex-col items-center px-6 py-16 text-center">
                <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-soft text-primary"><Inbox size={22} /></span>
                <div className="text-sm font-semibold text-text-primary">{tab === 'needs_team' ? 'Nothing waiting for your team' : 'No conversations yet'}</div>
                <p className="mt-1 text-xs text-text-muted">{tab === 'needs_team' ? 'Chats the AI hands over, or that you take over, show up here.' : 'Website and app chats appear here as they happen.'}</p>
                {agents.length === 0 && <Link to="/ai-agents" className="mt-3 text-xs font-medium text-primary hover:underline">Set up an AI agent</Link>}
              </div>
            ) : (
              rows.map((c) => (
                <button key={c.conversationUid} type="button" onClick={() => open(c.conversationUid)}
                  className={clsx('flex w-full gap-3 border-b border-border px-3 py-3 text-left transition-colors hover:bg-surface-alt', c.conversationUid === conversationUid && 'bg-primary-soft')}>
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-alt text-xs font-semibold text-text-secondary">
                    {who(c).trim()[0]?.toUpperCase() ?? '?'}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className={clsx('truncate text-sm', c.unreadForTeam ? 'font-semibold text-text-primary' : 'font-medium text-text-primary')}>{who(c)}</span>
                      <span className="ml-auto shrink-0 text-[11px] text-text-muted">{c.lastMessageAt ? formatDistanceToNowStrict(new Date(c.lastMessageAt)) : ''}</span>
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-text-muted">{c.lastMessagePreview}</span>
                    <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <ChannelBadge channel={c.channel} />
                      {c.aiPaused && c.status === 'open' && <span className="rounded-full bg-warning-soft px-1.5 py-0.5 text-[10px] font-medium text-warning">Team</span>}
                      {c.status === 'closed' && <span className="rounded-full bg-surface-alt px-1.5 py-0.5 text-[10px] font-medium text-text-muted">Closed</span>}
                      {agentName.get(c.agentUid) && !agentUid && <span className="truncate text-[10px] text-text-muted">{agentName.get(c.agentUid)}</span>}
                      {c.unreadForTeam > 0 && <span className="ml-auto rounded-full bg-primary px-1.5 text-[10px] font-semibold leading-4 text-on-primary">{c.unreadForTeam}</span>}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </aside>
        <section className={clsx('min-w-0 flex-1 flex-col bg-surface-alt/40', conversationUid ? 'flex' : 'hidden md:flex')}>
          {conversationUid
            ? <Thread key={conversationUid} uid={conversationUid} agentName={agentName} onBack={() => setParam('c', undefined, true)} onChanged={load} />
            : <div className="m-auto flex flex-col items-center p-8 text-center text-sm text-text-muted"><Bot size={28} className="mb-2 text-text-muted" />Pick a conversation to read it and reply.</div>}
        </section>
      </div>
    </div>
  );
}

function ChannelBadge({ channel }: { channel: AiConversation['channel'] }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-info-soft px-1.5 py-0.5 text-[10px] font-medium text-info">
      {channel === 'api' ? <Code2 size={10} /> : <Globe size={10} />}{channel === 'api' ? 'App' : 'Website'}
    </span>
  );
}

function Thread({ uid, agentName, onBack, onChanged }: { uid: string; agentName: Map<string, string>; onBack: () => void; onChanged: () => void }) {
  const [data, setData] = useState<{ conversation: AiConversation; messages: AiMessage[] } | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);
  const lastCount = useRef(0);

  const load = useCallback(() => aiAgentsService.conversation(uid).then(setData).catch(() => undefined), [uid]);
  useEffect(() => {
    void load().then(onChanged);
    const t = setInterval(load, 5_000);
    return () => clearInterval(t);
  }, [load, onChanged]);
  useEffect(() => {
    const n = data?.messages.length ?? 0;
    if (n !== lastCount.current) { lastCount.current = n; logRef.current?.scrollTo({ top: logRef.current.scrollHeight }); }
  }, [data]);

  if (!data) return <div className="m-auto text-sm text-text-muted">Loading…</div>;
  const { conversation: c, messages } = data;

  const act = async (fn: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    try { await fn(); await load(); onChanged(); if (done) toast.success(done); } catch (e: any) { if (!e?.response) toast.error('Something went wrong'); } finally { setBusy(false); }
  };
  const send = () => {
    const t = text.trim();
    if (!t) return;
    void act(async () => { await aiAgentsService.reply(uid, t); setText(''); });
  };
  const fields = Object.entries(c.collectedFields ?? {}).filter(([k]) => !k.startsWith('__'));

  return (
    <>
      <header className="flex flex-wrap items-center gap-3 border-b border-border bg-surface px-4 py-3">
        <button type="button" onClick={onBack} className="rounded-lg p-1.5 text-text-muted hover:bg-surface-alt md:hidden" aria-label="Back to conversations"><ArrowLeft size={16} /></button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-text-primary">{who(c)}</span>
            <ChannelBadge channel={c.channel} />
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-text-muted">
            {c.visitor?.phone && <span className="inline-flex items-center gap-1"><Phone size={11} />{c.visitor.phone}</span>}
            {c.visitor?.email && <span className="inline-flex items-center gap-1"><Mail size={11} />{c.visitor.email}</span>}
            {agentName.get(c.agentUid) && <span className="inline-flex items-center gap-1"><Bot size={11} />{agentName.get(c.agentUid)}</span>}
            {c.visitor?.pageUrl && <a href={c.visitor.pageUrl} target="_blank" rel="noreferrer" className="inline-flex max-w-[220px] items-center gap-1 truncate hover:text-primary"><ExternalLink size={11} />{c.visitor.pageUrl.replace(/^https?:\/\//, '')}</a>}
            {c.externalKey && <span>User id {c.externalKey}</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {c.leadUid && <Link to={`/leads/${c.leadUid}`}><Button variant="secondary" size="sm" iconLeft={<UserRound size={14} />}>Lead</Button></Link>}
          {c.aiPaused
            ? <Button variant="secondary" size="sm" iconLeft={<Play size={14} />} loading={busy} onClick={() => act(() => aiAgentsService.setAiPaused(uid, false), 'AI is answering again')}>Resume AI</Button>
            : <Button variant="secondary" size="sm" iconLeft={<Pause size={14} />} loading={busy} onClick={() => act(() => aiAgentsService.setAiPaused(uid, true), 'AI paused for this chat')}>Pause AI</Button>}
          {c.status === 'open'
            ? <Button variant="ghost" size="sm" iconLeft={<CheckCircle2 size={14} />} disabled={busy} onClick={() => act(() => aiAgentsService.setStatus(uid, 'closed'), 'Conversation closed')}>Close</Button>
            : <Button variant="ghost" size="sm" iconLeft={<RotateCcw size={14} />} disabled={busy} onClick={() => act(() => aiAgentsService.setStatus(uid, 'open'))}>Reopen</Button>}
        </div>
      </header>

      {c.aiPaused && (
        <div className="flex flex-wrap items-center gap-2 border-b border-warning/30 bg-warning-soft px-4 py-2 text-xs text-warning">
          <Pause size={13} className="shrink-0" />
          <span className="font-medium">{c.pausedReason ? PAUSE_REASON_LABEL[c.pausedReason] : 'AI paused'}</span>
          <span className="text-text-secondary">— the AI won't reply until you resume it.</span>
          {c.pausedReason === 'ai_failed' && c.lastError && <span className="w-full truncate text-text-muted" title={c.lastError}>Reason: {c.lastError}</span>}
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <div ref={logRef} className="min-w-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {messages.map((m) => <Bubble key={m.messageUid} m={m} />)}
        </div>
        {fields.length > 0 && (
          <aside className="hidden w-60 shrink-0 overflow-y-auto border-l border-border bg-surface p-4 xl:block">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-muted">Collected by the AI</div>
            <dl className="space-y-2">
              {fields.map(([k, v]) => (
                <div key={k}>
                  <dt className="text-[11px] text-text-muted">{k.replace(/_/g, ' ')}</dt>
                  <dd className="break-words text-sm text-text-primary">{v}</dd>
                </div>
              ))}
            </dl>
          </aside>
        )}
      </div>

      <div className="border-t border-border bg-surface p-3">
        <div className="flex items-end gap-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={2}
            maxLength={2000}
            placeholder={c.channel === 'api' ? 'Reply — the app shows it the next time it polls' : 'Reply — the visitor sees it in the chat within seconds'}
            className="min-w-0 flex-1 resize-none rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-primary"
            style={{ outline: 'none' }}
            aria-label="Reply"
          />
          <Button iconLeft={<Send size={14} />} loading={busy} disabled={!text.trim()} onClick={send}>Send</Button>
        </div>
        {!c.aiPaused && <p className="mt-1.5 text-[11px] text-text-muted">Sending a reply takes the chat over — the AI pauses until you resume it.</p>}
      </div>
    </>
  );
}

function Bubble({ m }: { m: AiMessage }) {
  if (m.role === 'system') return <div className="text-center text-[11px] text-text-muted">{m.body}</div>;
  const customer = m.role === 'customer';
  const label = customer ? null : m.role === 'agent' ? 'Team' : m.source === 'greeting' ? 'Greeting' : m.source === 'fallback' ? 'Holding reply (AI failed)' : m.source === 'injection_refused' ? 'Refused (prompt injection)' : 'AI';
  return (
    <div className={clsx('flex flex-col', customer ? 'items-start' : 'items-end')}>
      {label && <span className="mb-0.5 text-[10px] font-medium text-text-muted">{label}</span>}
      <div className={clsx(
        'max-w-[78%] whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm',
        customer ? 'rounded-bl-sm border border-border bg-surface text-text-primary'
          : m.role === 'agent' ? 'rounded-br-sm bg-gold-gradient text-on-primary'
            : 'rounded-br-sm bg-primary-soft text-text-primary',
      )}>
        {m.body}
      </div>
      {!!m.sources?.length && (
        <div className="mt-1 flex max-w-[78%] flex-wrap justify-end gap-1">
          {m.sources.map((s) => s.url
            ? <a key={`${s.sourceUid}${s.url}`} href={s.url} target="_blank" rel="noreferrer" className="inline-flex max-w-[220px] items-center gap-1 truncate rounded-full bg-surface px-2 py-0.5 text-[10px] text-text-muted ring-1 ring-border hover:text-primary"><BookOpen size={10} />{s.title}</a>
            : <span key={s.sourceUid} className="inline-flex max-w-[220px] items-center gap-1 truncate rounded-full bg-surface px-2 py-0.5 text-[10px] text-text-muted ring-1 ring-border"><BookOpen size={10} />{s.title}</span>)}
        </div>
      )}
      <span className="mt-0.5 text-[10px] text-text-muted" title={format(new Date(m.createdAt), 'PPpp')}>
        {format(new Date(m.createdAt), 'HH:mm')}
        {!!m.credits && <span title="Managed AI credits this reply cost"> · {m.credits < 0.01 ? '<0.01' : m.credits.toFixed(2)} credits</span>}
      </span>
    </div>
  );
}
